import { Fragment, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { SearchField, Spinner, Pill, FormGrid, Field, TextInput, NumberInput, DateInput, SelectInput } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can, rolesOf, BIZ_ROLES } from '../../lib/permissions';
import { num, stockMovementType, dateLabel } from '../../lib/format';
import type { StockBalance, StockMovement, StockMovementType, Item, Warehouse, StockWarningItem, StockCount, StockLedgerMovement, StockLedgerResponse } from '../../lib/types';

// 现场口径：业务按钮自动生成流水，技术类型对用户隐藏。
// 保留入库/出库/调拨/调整四类业务动作；生产/维修流水由工单按钮产生。
const MOVEMENT_TYPES: StockMovementType[] = [
  'receipt', 'issue', 'transfer', 'adjustment',
];
// 流水查询页签：系统各入口产生的全部类型（含生产/维修自动过账），供筛选与展示。
const ALL_MOVEMENT_TYPES: StockMovementType[] = [
  'receipt', 'issue', 'transfer', 'adjustment', 'delivery', 'customer_return', 'supplier_return',
  'production_consume', 'production_return', 'production_output',
];
// 方向提示：入=只进目标仓，出=只扣来源仓，调拨=两者都要。
function directionHint(t: StockMovementType): 'in' | 'out' | 'both' | 'any' {
  if (t === 'receipt' || t === 'customer_return' || t === 'production_output') return 'in';
  if (t === 'issue' || t === 'delivery' || t === 'supplier_return' || t === 'production_consume') return 'out';
  if (t === 'transfer') return 'both';
  return 'any';
}

const today = () => new Date().toISOString().slice(0, 10);

const POLICY_LABELS: Record<string, string> = {
  powder: '粉末', consumable: '耗材', normal: '普通', none: '不进库存',
};

interface MoveLine {
  itemId: string; itemLabel: string; quantity: string; uom: string;
  fromId: string; fromLabel: string; toId: string; toLabel: string; batch: string;
}
const emptyLine = (): MoveLine => ({ itemId: '', itemLabel: '', quantity: '1', uom: 'Unidad', fromId: '', fromLabel: '', toId: '', toLabel: '', batch: '' });

function NewMovementForm({ onCreated, onCancel }: { onCreated: (m: StockMovement) => void; onCancel: () => void }) {
  const [type, setType] = useState<StockMovementType>('receipt');
  const [date, setDate] = useState(today());
  const [referenceType, setReferenceType] = useState('');
  const [notes, setNotes] = useState('');
  const [sourceSystem, setSourceSystem] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [lines, setLines] = useState<MoveLine[]>([emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dir = directionHint(type);

  const setLine = (i: number, patch: Partial<MoveLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    setErr(null);
    if (!date) { setErr('请填写业务日期'); return; }
    const valid = lines.filter((l) => l.itemId && Number(l.quantity) > 0 && (l.fromId || l.toId));
    if (!valid.length) { setErr('至少一行有效明细：选择物料、数量>0，并至少指定来源或目标仓库'); return; }
    setBusy(true);
    try {
      const res = await api.post<{ data: StockMovement }>('/api/stock/movements', {
        movement_type: type, movement_date: date,
        reference_type: referenceType || null, notes: notes || null,
        source_system: sourceSystem || null, source_id: sourceId || null,
        lines: valid.map((l) => ({
          item_id: l.itemId, quantity: Number(l.quantity), uom: l.uom || 'Unidad',
          from_warehouse_id: l.fromId || null, to_warehouse_id: l.toId || null, batch_number: l.batch || null,
        })),
      });
      onCreated(res.data);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '过账失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>新建库存流水</h2><div className="desc">过账到核心服务 /api/stock/movements · 追加式流水，聚合即余额；库存不足会由后端拦截（409）。粉末自动折算为 g</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid min={180}>
          <Field label="流水类型" required>
            <SelectInput value={type} onChange={(e) => setType(e.target.value as StockMovementType)}>
              {MOVEMENT_TYPES.map((t) => <option key={t} value={t}>{stockMovementType(t).label}</option>)}
            </SelectInput>
          </Field>
          <Field label="业务日期" required><DateInput value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="来源类型（选填）"><TextInput value={referenceType} onChange={(e) => setReferenceType(e.target.value)} placeholder="work_order / delivery / po …" /></Field>
          <Field label="来源单号 / 幂等键（source_id 选填）"><TextInput value={sourceId} onChange={(e) => setSourceId(e.target.value)} placeholder="跨系统重复过账防重" /></Field>
          <Field label="来源系统（source_system 选填）"><TextInput value={sourceSystem} onChange={(e) => setSourceSystem(e.target.value)} placeholder="qc / factura / erpnext" /></Field>
          <Field label="备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </FormGrid>

        <div className="detail-head" style={{ margin: '12px 0 6px' }}>
          <h3 style={{ fontSize: 14 }}>明细（{dir === 'in' ? '入库：填目标仓' : dir === 'out' ? '出库：填来源仓' : dir === 'both' ? '调拨：来源 → 目标' : '按需填来源/目标'}）</h3>
          <div className="spacer" />
          <button className="btn link" onClick={() => setLines((ls) => [...ls, emptyLine()])}>+ 添加明细</button>
        </div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr .8fr .8fr 1.2fr 1.2fr 1fr auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`物料 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.itemLabel}
                onChange={(row) => setLine(i, {
                  itemId: row?.id ?? '', itemLabel: row ? `${row.code} ${row.name_zh}` : '',
                  uom: row?.inventory_policy === 'powder' ? 'g' : (row?.stock_uom || l.uom),
                })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
            </Field>
            <Field label="数量"><NumberInput value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></Field>
            <Field label="单位"><TextInput value={l.uom} onChange={(e) => setLine(i, { uom: e.target.value })} /></Field>
            {(dir === 'out' || dir === 'both' || dir === 'any') ? (
              <Field label="来源仓库">
                <SearchSelect<Warehouse> endpoint="/api/warehouses" value={l.fromId || null} label={l.fromLabel}
                  onChange={(row) => setLine(i, { fromId: row?.id ?? '', fromLabel: row ? `${row.code} ${row.name_zh}` : '' })}
                  optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="来源…" />
              </Field>
            ) : <span />}
            {(dir === 'in' || dir === 'both' || dir === 'any') ? (
              <Field label="目标仓库">
                <SearchSelect<Warehouse> endpoint="/api/warehouses" value={l.toId || null} label={l.toLabel}
                  onChange={(row) => setLine(i, { toId: row?.id ?? '', toLabel: row ? `${row.code} ${row.name_zh}` : '' })}
                  optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="目标…" />
              </Field>
            ) : <span />}
            <Field label="批次(选填)"><TextInput value={l.batch} onChange={(e) => setLine(i, { batch: e.target.value })} /></Field>
            <button className="btn" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} title="删除该明细">×</button>
          </div>
        ))}
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '过账库存流水'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

interface CountLine {
  itemId: string; itemLabel: string; warehouseId: string; warehouseLabel: string;
  actualQty: string; reason: string; uom: string;
}
const emptyCountLine = (): CountLine => ({
  itemId: '', itemLabel: '', warehouseId: '', warehouseLabel: '', actualQty: '0', reason: '', uom: '',
});

interface ConsumableLine {
  itemId: string; itemLabel: string; warehouseId: string; warehouseLabel: string;
  quantity: string; uom: string; notes: string;
}
const emptyConsumable = (): ConsumableLine => ({
  itemId: '', itemLabel: '', warehouseId: '', warehouseLabel: '', quantity: '1', uom: '', notes: '',
});

function ConsumableUsageForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [date, setDate] = useState(today());
  const [operator, setOperator] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ConsumableLine[]>([emptyConsumable()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setLine = (i: number, patch: Partial<ConsumableLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    setErr(null);
    const valid = lines.filter((l) => l.itemId && l.warehouseId && Number(l.quantity) > 0);
    if (!valid.length) { setErr('至少一行：耗材 + 仓库 + 数量'); return; }
    setBusy(true);
    try {
      // 整次提交一个事件 ID：多行一张单、单事务；超时重试幂等不重复扣库存
      const eventId = `CU-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await api.post('/api/stock/consumable-usages', {
        source_system: 'web-console',
        source_id: eventId,
        used_date: date,
        operator_name: operator || null,
        notes: notes || null,
        lines: valid.map((l) => ({
          item_id: l.itemId,
          warehouse_id: l.warehouseId,
          quantity: Number(l.quantity),
          uom: l.uom || null,
          notes: l.notes || null,
        })),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '记录使用失败（整单已回滚，库存未扣）');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>耗材使用</h2>
          <div className="desc">砂轮 / 抛光膏 / 刀具等：填实际使用量，自动扣原材料库存。不进 BOM、不摊单件成本</div>
        </div>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid min={180}>
          <Field label="使用日期" required><DateInput value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="使用人"><TextInput value={operator} onChange={(e) => setOperator(e.target.value)} /></Field>
          <Field label="备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="用途 / 机台 / 班次…" /></Field>
        </FormGrid>
        <div className="detail-head" style={{ margin: '12px 0 6px' }}>
          <h3 style={{ fontSize: 14 }}>使用明细</h3>
          <div className="spacer" />
          <button className="btn link" onClick={() => setLines((ls) => [...ls, emptyConsumable()])}>+ 添加</button>
        </div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.2fr .7fr .7fr 1.2fr auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`耗材 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.itemLabel}
                onChange={(row) => setLine(i, {
                  itemId: row?.id ?? '', itemLabel: row ? `${row.code} ${row.name_zh}` : '',
                  uom: row?.stock_uom || l.uom,
                })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索耗材…" />
            </Field>
            <Field label="仓库">
              <SearchSelect<Warehouse> endpoint="/api/warehouses" value={l.warehouseId || null} label={l.warehouseLabel}
                onChange={(row) => setLine(i, { warehouseId: row?.id ?? '', warehouseLabel: row ? `${row.code} ${row.name_zh}` : '' })}
                optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="仓库…" />
            </Field>
            <Field label="数量"><NumberInput value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></Field>
            <Field label="单位"><TextInput value={l.uom} onChange={(e) => setLine(i, { uom: e.target.value })} /></Field>
            <Field label="备注"><TextInput value={l.notes} onChange={(e) => setLine(i, { notes: e.target.value })} /></Field>
            <button className="btn" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} title="删除">×</button>
          </div>
        ))}
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '记录使用并扣库存'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

function StockCountForm({ onCreated, onCancel }: { onCreated: (c: StockCount) => void; onCancel: () => void }) {
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<CountLine[]>([emptyCountLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setLine = (i: number, patch: Partial<CountLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    setErr(null);
    const valid = lines.filter((l) => l.itemId && l.warehouseId && Number(l.actualQty) >= 0);
    if (!valid.length) { setErr('至少一行有效明细：物料 + 仓库 + 实盘数'); return; }
    setBusy(true);
    try {
      const created = await api.post<{ data: StockCount }>('/api/stock/counts', {
        count_date: date,
        notes: notes || null,
        lines: valid.map((l) => ({
          item_id: l.itemId,
          warehouse_id: l.warehouseId,
          actual_qty: Number(l.actualQty),
          reason: l.reason || null,
          uom: l.uom || null,
        })),
      });
      onCreated(created.data);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建盘点单失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>库存盘点</h2>
          <div className="desc">录入实盘数，系统按账面余额计算差异；确认后生成 adjustment 流水。砂轮/抛光膏等耗材建议按月或季度盘点，不必追到具体维修工单</div>
        </div>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid min={180}>
          <Field label="盘点日期" required><DateInput value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="例如：2026-10 机加耗材月盘" /></Field>
        </FormGrid>
        <div className="detail-head" style={{ margin: '12px 0 6px' }}>
          <h3 style={{ fontSize: 14 }}>盘点明细</h3>
          <div className="spacer" />
          <button className="btn link" onClick={() => setLines((ls) => [...ls, emptyCountLine()])}>+ 添加明细</button>
        </div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.2fr .8fr 1.2fr auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`物料 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.itemLabel}
                onChange={(row) => setLine(i, {
                  itemId: row?.id ?? '', itemLabel: row ? `${row.code} ${row.name_zh}` : '',
                  uom: row?.inventory_policy === 'powder' ? 'g' : (row?.stock_uom || l.uom),
                })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
            </Field>
            <Field label="仓库">
              <SearchSelect<Warehouse> endpoint="/api/warehouses" value={l.warehouseId || null} label={l.warehouseLabel}
                onChange={(row) => setLine(i, { warehouseId: row?.id ?? '', warehouseLabel: row ? `${row.code} ${row.name_zh}` : '' })}
                optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="仓库…" />
            </Field>
            <Field label="实盘数"><NumberInput value={l.actualQty} onChange={(e) => setLine(i, { actualQty: e.target.value })} /></Field>
            <Field label="差异原因"><TextInput value={l.reason} onChange={(e) => setLine(i, { reason: e.target.value })} /></Field>
            <button className="btn" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} title="删除">×</button>
          </div>
        ))}
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '创建中…' : '创建盘点单'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

function StockWarningsPanel({ warnings, loading, error, onRetry }: {
  warnings: StockWarningItem[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const alerts = (warnings || []).filter((w) => w.level === 'red' || w.level === 'orange');
  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>库存预警</h2>
          <div className="desc">
            红：余额 ≤ 最低库存 · 橙：余额 − 未完工计划粉末 ≤ 最低库存 · 同时显示采购在途。零库存重点物料也会列出
          </div>
        </div>
        <div className="spacer" />
        <button className="btn" onClick={onRetry} disabled={loading}>刷新</button>
      </div>
      <div className="panel-body">
        {loading && !warnings ? <Spinner label="加载库存预警…" /> : null}
        {error ? <div className="notice bad">{error}</div> : null}
        {!loading && !error && alerts.length === 0 ? (
          <div className="muted" style={{ fontSize: 12.5 }}>当前无红色/橙色预警。</div>
        ) : null}
        {alerts.length > 0 ? (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>级别</th><th>物料</th><th>策略</th>
                  <th style={{ textAlign: 'right' }}>当前余额</th>
                  <th style={{ textAlign: 'right' }}>未完工计划</th>
                  <th style={{ textAlign: 'right' }}>预计可用</th>
                  <th style={{ textAlign: 'right' }}>采购在途</th>
                  <th style={{ textAlign: 'right' }}>最低库存</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((w) => (
                  <tr key={w.item_id}>
                    <td>
                      <Pill
                        tone={w.level === 'red' ? 'bad' : 'warn'}
                        label={w.level === 'red' ? '红色 · 需补货' : '橙色 · 计划后不足'}
                      />
                    </td>
                    <td>
                      <span className="num strong">{w.item_code}</span>
                      <div className="muted" style={{ fontSize: 12 }}>{w.name_zh}</div>
                    </td>
                    <td><span className="muted">{POLICY_LABELS[w.inventory_policy] || w.inventory_policy}</span></td>
                    <td style={{ textAlign: 'right' }}><span className="num">{num(w.quantity, 3)}</span> <span className="muted">{w.stock_uom}</span></td>
                    <td style={{ textAlign: 'right' }}><span className="num">{num(w.open_plan_qty, 3)}</span></td>
                    <td style={{ textAlign: 'right' }}><span className="num">{num(w.projected_qty, 3)}</span></td>
                    <td style={{ textAlign: 'right' }}><span className="num">{num(w.in_transit_qty, 3)}</span></td>
                    <td style={{ textAlign: 'right' }}><span className="num">{num(w.minimum_qty, 3)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function AdjustModal({ row, initDelta, onClose, onDone }: {
  row: StockBalance; initDelta: number; onClose: () => void; onDone: () => void;
}) {
  const uom = row.inventory_policy === 'powder' ? 'g' : (row.stock_uom || 'Unidad');
  const [delta, setDelta] = useState(String(initDelta));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dnum = Number(delta);
  const finite = Number.isFinite(dnum);
  const after = row.quantity + (finite ? dnum : 0);
  const canSubmit = finite && dnum !== 0 && !!reason.trim() && after >= -1e-9;

  const submit = async () => {
    setErr(null);
    if (!finite || dnum === 0) { setErr('请输入非零调整数量（正=增加，负=减少）'); return; }
    if (!reason.trim()) { setErr('请填写调整原因（写入审计）'); return; }
    if (after < -1e-9) { setErr(`调整后余额为负（最多可减少 ${num(row.quantity, 3)} ${uom}）`); return; }
    setBusy(true);
    try {
      const line = dnum > 0
        ? { item_id: row.item_id, to_warehouse_id: row.warehouse_id, quantity: dnum, uom }
        : { item_id: row.item_id, from_warehouse_id: row.warehouse_id, quantity: -dnum, uom };
      await api.post('/api/stock/movements', {
        movement_type: 'adjustment', movement_date: today(),
        reference_type: 'manual_adjust', notes: reason.trim(), lines: [line],
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '过账调整失败');
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="库存调整" style={{ width: 'min(540px,94vw)' }}>
        <div className="modal-head">库存调整 · {row.item_code}</div>
        <div className="modal-body">
          {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
          <div className="kv"><span className="k">物料</span><span className="v">{row.item_code} · {row.name_zh}</span></div>
          <div className="kv"><span className="k">仓库</span><span className="v">{row.warehouse_code} · {row.warehouse_name}</span></div>
          <div className="kv"><span className="k">当前余额</span><span className="v num">{num(row.quantity, 3)} {uom}</span></div>
          <div className="desc" style={{ margin: '8px 0 12px' }}>
            以「调整」流水纠正该仓位余额：正数增加、负数减少。不改写历史流水，调整后余额即时生效。
          </div>
          <FormGrid min={200}>
            <Field label="调整数量（正=增加，负=减少）" required>
              <NumberInput value={delta} step="any" onChange={(e) => setDelta(e.target.value)} />
            </Field>
            <Field label="调整后余额">
              <TextInput value={`${num(after, 3)} ${uom}`} disabled readOnly />
            </Field>
            <Field label="调整原因" required span>
              <TextInput value={reason} placeholder="如：盘点纠错 / 破损报废 / 录入错误" onChange={(e) => setReason(e.target.value)} />
            </Field>
          </FormGrid>
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button className="btn" onClick={onClose} disabled={busy}>取消</button>
          <button className="btn primary" onClick={submit} disabled={busy || !canSubmit}>{busy ? '过账中…' : '过账调整流水'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------- 库存流水（查询 + 受控清理） ----------
// 此前本页只有余额：删除拦截报「存在 N 条关联库存流水，请先处理这些单据」时无处可去。
// 流水页签补齐入口：按流水号 / 物料 / 工单号 / 备注检索，管理员可删除单条流水；
// 删除由后端同步回退工单完工数量与领退料快照（不能只删流水明细），余额随流水聚合自动恢复。
// 支持深链 /stock?tab=ledger&q=STK-xxxx —— 删除拦截弹窗里的「查看关联流水」跳这里。
function movementSourceLabel(m: StockLedgerMovement): string {
  if (m.reference_type === 'stock_count') return '库存盘点';
  if (m.reference_type === 'consumable_usage') return '耗材使用';
  if (m.reference_type === 'manual_adjust') return '手工调整';
  if (m.reference_type === 'erpnext_balance_correction') return 'ERPNext 余额纠正';
  if (m.reference_type === 'purchase_order') return '采购收货';
  return m.reference_type || '手工过账';
}

function ledgerLineText(l: NonNullable<StockLedgerMovement['lines']>[number]): string {
  const dir = l.from_warehouse_code
    ? `${l.from_warehouse_code} → ${l.to_warehouse_code ?? '—'}`
    : `→ ${l.to_warehouse_code ?? '—'}`;
  return `${l.item_code ?? l.item_id} ×${num(l.quantity, 3)} ${l.uom}（${dir}）`;
}

function DeleteMovementDialog({ movement, onClose, onDone }: {
  movement: StockLedgerMovement;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true); setErr(null);
    try {
      await api.del(`/api/stock/movements/${movement.id}`);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '删除失败');
    } finally { setBusy(false); }
  };

  return (
    <ConfirmDialog
      open
      title={`删除库存流水 ${movement.number}`}
      message="确认删除该流水？此操作不可恢复，并写入审计日志。"
      detail={
        <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
          {err ? <div style={{ color: 'var(--bad)' }} role="alert">{err}</div> : null}
          <div>
            {stockMovementType(movement.movement_type).label}
            {movement.work_order_number ? ` · 来源工单 ${movement.work_order_number}` : ''}
            {` · ${dateLabel(movement.movement_date)}`}
          </div>
          <div className="muted">
            余额由流水聚合得出，删除后自动恢复；来自工单的流水会同步回退完工数量 / 领退料快照，
            不能只删流水明细。交货单、退货单产生的流水请删除对应单据。
          </div>
        </div>
      }
      tone="danger"
      confirmLabel="确认删除"
      busy={busy}
      onConfirm={remove}
      onCancel={onClose}
    />
  );
}

function StockLedgerPanel({ onChanged }: { onChanged: () => void }) {
  const { user, isAdmin } = useAuth();
  // 删除入口与后端路由同源：管理员 + 业务操作员（BIZ_ROLES，统一身份 engineer 展开即此口径）。
  const roles = rolesOf(user);
  const canDelete = isAdmin || BIZ_ROLES.some((r) => roles.includes(r));
  const [params] = useSearchParams();
  const urlQ = params.get('q') ?? '';
  const [q, setQ] = useState(urlQ);
  const [applied, setApplied] = useState(urlQ);
  const [type, setType] = useState('');
  const [rows, setRows] = useState<StockLedgerMovement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<StockLedgerMovement | null>(null);

  // 深链参数变化（删除拦截弹窗跳转）时同步关键字并重查
  useEffect(() => { setQ(urlQ); setApplied(urlQ); }, [urlQ]);

  const load = useCallback(async (kw: string, mtype: string) => {
    setLoading(true); setError(null);
    try {
      const usp = new URLSearchParams();
      if (kw) usp.set('q', kw);
      if (mtype) usp.set('movement_type', mtype);
      usp.set('limit', '200');
      const r = await api.get<StockLedgerResponse>(`/api/stock/movements?${usp.toString()}`);
      setRows(r.data || []);
      setTotal(r.total ?? (r.data?.length ?? 0));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载库存流水失败');
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(applied, type); }, [applied, type, load]);

  return (
    <>
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>库存流水</h2>
            <div className="desc">全部出入库记录 · 余额由 posted 流水聚合 · /api/stock/movements · 管理员可受控删除（同步回退完工数量与库存影响）</div>
          </div>
          <div className="spacer" />
          <button className="btn" onClick={() => void load(applied, type)} disabled={loading}>刷新</button>
        </div>

        <div className="toolbar" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label>关键字</label>
            <SearchField value={q} onChange={setQ} placeholder="流水号 / 物料编码 / 工单号 / 备注…" />
          </div>
          <div className="field">
            <label>类型</label>
            <SelectInput value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">全部类型</option>
              {ALL_MOVEMENT_TYPES.map((t) => <option key={t} value={t}>{stockMovementType(t).label}</option>)}
            </SelectInput>
          </div>
          <div className="field">
            <label>操作</label>
            <button className="btn primary" onClick={() => setApplied(q.trim())} disabled={loading}>查询</button>
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div style={{ padding: 'var(--pad)' }}><Spinner label="正在读取库存流水…" /></div>
        ) : error ? (
          <div style={{ padding: 'var(--pad)' }}><div className="notice bad" role="alert">{error}</div></div>
        ) : rows.length === 0 ? (
          <div className="empty">没有匹配的库存流水{applied || type ? '（当前筛选无匹配）' : ''}。</div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>流水号</th><th>类型</th><th>日期</th><th>来源</th>
                  <th>明细</th><th>备注</th>
                  {canDelete ? <th>操作</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const lines = m.lines ?? [];
                  const t = stockMovementType(m.movement_type);
                  return (
                    <Fragment key={m.id}>
                      <tr
                        onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                        style={{ cursor: 'pointer' }}
                        title="点击展开明细"
                      >
                        <td><span className="num strong">{m.number}</span></td>
                        <td><Pill tone={t.tone} label={t.label} /></td>
                        <td><span className="muted">{dateLabel(m.movement_date)}</span></td>
                        <td>
                          {m.work_order_number
                            ? <span className="num">{m.work_order_number}</span>
                            : <span className="muted">{movementSourceLabel(m)}</span>}
                        </td>
                        <td style={{ maxWidth: 380 }}>
                          {lines.length ? (
                            <span style={{ fontSize: 12.5 }}>
                              {ledgerLineText(lines[0])}
                              {lines.length > 1 ? ` 等 ${lines.length} 行` : ''}
                            </span>
                          ) : '—'}
                        </td>
                        <td><span className="muted" style={{ fontSize: 12.5 }}>{m.notes || '—'}</span></td>
                        {canDelete ? (
                          <td onClick={(e) => e.stopPropagation()}>
                            <button className="btn link row-del" onClick={() => setDeleting(m)}>删除</button>
                          </td>
                        ) : null}
                      </tr>
                      {expanded === m.id ? (
                        <tr>
                          <td colSpan={canDelete ? 7 : 6} style={{ background: 'var(--panel-inset)' }}>
                            <table className="grid">
                              <thead>
                                <tr>
                                  <th>物料</th><th style={{ textAlign: 'right' }}>数量</th><th>单位</th>
                                  <th>方向</th><th>批次</th><th>工件</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lines.map((l) => (
                                  <tr key={l.id}>
                                    <td><span className="num strong">{l.item_code}</span> · {l.item_name_zh}</td>
                                    <td style={{ textAlign: 'right' }}><span className="num">{num(l.quantity, 3)}</span></td>
                                    <td><span className="muted">{l.uom}</span></td>
                                    <td><span className="muted">{l.from_warehouse_code ? `${l.from_warehouse_code} → ` : ''}{l.to_warehouse_code ?? '—'}</span></td>
                                    <td><span className="muted">{l.batch_number || '—'}</span></td>
                                    <td><span className="muted">{l.asset_serial || '—'}</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="pager">
          <span className="info">{loading ? '加载中…' : `共 ${total} 条流水`}</span>
        </div>
      </section>

      {deleting ? (
        <DeleteMovementDialog
          movement={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); void load(applied, type); onChanged(); }}
        />
      ) : null}
    </>
  );
}

export function StockPage() {
  const { user } = useAuth();
  const canWrite = can(user, 'stock/movements', 'create');
  const [params, setParams] = useSearchParams();
  // tab=ledger 深链（删除拦截弹窗「查看关联流水」）；缺省=余额
  const tab = params.get('tab') === 'ledger' ? 'ledger' : 'balances';
  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [warnings, setWarnings] = useState<StockWarningItem[] | null>(null);
  const [warnLoading, setWarnLoading] = useState(true);
  const [warnError, setWarnError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [counting, setCounting] = useState(false);
  const [usingConsumable, setUsingConsumable] = useState(false);
  const [posted, setPosted] = useState<StockMovement | null>(null);
  const [counted, setCounted] = useState<StockCount | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [adjust, setAdjust] = useState<{ row: StockBalance; initDelta: number } | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setBalances(await api.list<StockBalance>('/api/stock/balances'));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载库存余额失败');
      setBalances([]);
    } finally { setLoading(false); }
  };

  const loadWarnings = async () => {
    setWarnLoading(true); setWarnError(null);
    try {
      const data = await api.detail<{ items: StockWarningItem[] }>('/api/stock/warnings');
      setWarnings(data.items || []);
    } catch (e) {
      setWarnError(e instanceof ApiError ? e.message : '加载库存预警失败');
      setWarnings([]);
    } finally { setWarnLoading(false); }
  };

  useEffect(() => {
    void load();
    void loadWarnings();
  }, []);

  const confirmCount = async () => {
    if (!counted?.id) return;
    setConfirming(true);
    try {
      const res = await api.post<{ data: StockCount }>(`/api/stock/counts/${counted.id}/confirm`, {});
      setCounted(res.data);
      void load();
      void loadWarnings();
    } catch (e) {
      setWarnError(e instanceof ApiError ? e.message : '确认盘点失败');
    } finally { setConfirming(false); }
  };

  const kw = q.trim().toLowerCase();
  const rows = kw
    ? balances.filter((b) => `${b.item_code} ${b.name_zh} ${b.warehouse_code} ${b.warehouse_name}`.toLowerCase().includes(kw))
    : balances;

  return (
    <>
      <div className="panel" style={{ marginBottom: 'var(--gap)', boxShadow: 'none' }}>
        <div className="panel-head" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <h2 style={{ marginRight: 8 }}>库存</h2>
          <button className={`btn${tab === 'balances' ? ' primary' : ''}`} onClick={() => setParams({})}>余额</button>
          <button className={`btn${tab === 'ledger' ? ' primary' : ''}`} onClick={() => setParams({ tab: 'ledger' })}>库存流水</button>
          <div className="spacer" />
          <div className="desc" style={{ margin: 0 }}>余额 = 流水按仓聚合 · 流水可查询，管理员可受控清理（同步回退完工数量与库存影响）</div>
        </div>
      </div>

      {tab === 'ledger' ? (
        <StockLedgerPanel onChanged={() => { void load(); void loadWarnings(); }} />
      ) : (
      <>
      <StockWarningsPanel warnings={warnings} loading={warnLoading} error={warnError} onRetry={() => void loadWarnings()} />

      {creating ? (
        <NewMovementForm
          onCancel={() => setCreating(false)}
          onCreated={(m) => { setPosted(m); setCreating(false); void load(); void loadWarnings(); }}
        />
      ) : null}

      {counting ? (
        <StockCountForm
          onCancel={() => setCounting(false)}
          onCreated={(c) => { setCounted(c); setCounting(false); }}
        />
      ) : null}

      {usingConsumable ? (
        <ConsumableUsageForm
          onCancel={() => setUsingConsumable(false)}
          onCreated={() => { setUsingConsumable(false); void load(); void loadWarnings(); }}
        />
      ) : null}

      {counted && counted.status === 'draft' ? (
        <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
          <div className="panel-head">
            <div>
              <h2>盘点单 {counted.number}</h2>
              <div className="desc">确认后按差异自动生成 adjustment 流水（盘盈入库 / 盘亏出库）</div>
            </div>
          </div>
          <div className="panel-body">
            <div className="table-wrap">
              <table className="grid">
                <thead>
                  <tr>
                    <th>物料</th><th>仓库</th>
                    <th style={{ textAlign: 'right' }}>账面</th>
                    <th style={{ textAlign: 'right' }}>实盘</th>
                    <th style={{ textAlign: 'right' }}>差异</th>
                    <th>原因</th>
                  </tr>
                </thead>
                <tbody>
                  {(counted.lines || []).map((l) => (
                    <tr key={l.id || `${l.item_id}:${l.warehouse_id}`}>
                      <td><span className="num strong">{l.item_code}</span> · {l.item_name_zh}</td>
                      <td><span className="muted">{l.warehouse_code}</span></td>
                      <td style={{ textAlign: 'right' }}><span className="num">{num(l.book_qty, 3)}</span></td>
                      <td style={{ textAlign: 'right' }}><span className="num">{num(l.actual_qty, 3)}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="num" style={{ color: Math.abs(l.variance) > 1e-9 ? 'var(--bad, #c0392b)' : undefined }}>
                          {l.variance > 0 ? '+' : ''}{num(l.variance, 3)}
                        </span>
                      </td>
                      <td>{l.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="detail-actions">
              <button className="btn primary" onClick={() => void confirmCount()} disabled={confirming}>
                {confirming ? '确认中…' : '确认盘点并生成调整流水'}
              </button>
              <button className="btn" onClick={() => setCounted(null)} disabled={confirming}>关闭</button>
            </div>
          </div>
        </section>
      ) : null}

      {counted && counted.status === 'confirmed' ? (
        <div className="notice ok" style={{ marginBottom: 'var(--gap)' }}>
          盘点单 <b>{counted.number}</b> 已确认，差异已过账为 adjustment 流水。
          <button className="btn link" onClick={() => setCounted(null)} style={{ marginLeft: 8 }}>关闭</button>
        </div>
      ) : null}

      <section className="panel">
        <div className="panel-head">
          <div style={{ minWidth: 0 }}>
            <h2>库存余额</h2>
            <div className="desc">余额由追加式流水按仓位聚合 · 粉末按 g 折算 · /api/stock/balances</div>
          </div>
          <div className="spacer" />
          {canWrite ? (
            <>
              <button className="btn" onClick={() => setUsingConsumable((v) => !v)} style={{ marginRight: 8 }}>
                {usingConsumable ? '收起耗材' : '耗材使用'}
              </button>
              <button className="btn" onClick={() => setCounting((v) => !v)} style={{ marginRight: 8 }}>
                {counting ? '收起盘点' : '库存盘点'}
              </button>
              <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起过账' : '入库/调整'}</button>
            </>
          ) : null}
        </div>

        {posted ? (
          <div className="notice ok" style={{ margin: 'var(--pad) 0 0' }}>
            已过账库存流水 <b>{posted.number}</b> · <Pill tone={stockMovementType(posted.movement_type).tone} label={stockMovementType(posted.movement_type).label} /> · {posted.lines?.length ?? 0} 条明细（余额已刷新）
          </div>
        ) : null}

        <div className="toolbar" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label>关键字筛选</label>
            <SearchField value={q} onChange={setQ} placeholder="搜索物料编码 / 名称 / 仓库…" />
          </div>
          <div className="field">
            <label>操作</label>
            <button className="btn" onClick={() => { void load(); void loadWarnings(); }} disabled={loading} title="重新拉取">刷新</button>
          </div>
        </div>

        {loading && balances.length === 0 ? (
          <div style={{ padding: 'var(--pad)' }}><Spinner label="正在从核心服务读取库存…" /></div>
        ) : error ? (
          <div style={{ padding: 'var(--pad)' }}><div className="notice bad" role="alert">{error}</div></div>
        ) : rows.length === 0 ? (
          <div className="empty">暂无非零库存余额。零库存重点物料请看上方「库存预警」{kw ? '；当前筛选无匹配' : ''}。</div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>物料编码</th><th>物料名称</th><th>策略</th><th>仓库</th>
                  <th style={{ textAlign: 'right' }}>库存数量</th><th>单位</th>
                  {canWrite ? <th>操作</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={`${b.item_id}:${b.warehouse_id}`}>
                    <td><span className="num strong">{b.item_code}</span></td>
                    <td>{b.name_zh}</td>
                    <td><span className="muted">{POLICY_LABELS[b.inventory_policy || 'normal'] || '—'}</span></td>
                    <td><span className="muted">{b.warehouse_code}</span> · {b.warehouse_name}</td>
                    <td style={{ textAlign: 'right' }}><span className="num">{num(b.quantity, 3)}</span></td>
                    <td><span className="muted">{b.inventory_policy === 'powder' ? 'g' : (b.stock_uom || '')}</span></td>
                    {canWrite ? (
                      <td>
                        <button className="btn link" onClick={() => setAdjust({ row: b, initDelta: 0 })}>调整</button>
                        {b.quantity > 1e-9 ? (
                          <button className="btn link" onClick={() => setAdjust({ row: b, initDelta: -b.quantity })} style={{ marginLeft: 8 }}>清零</button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pager">
          <span className="info">{loading ? '加载中…' : `共 ${rows.length} 条余额`}</span>
        </div>
      </section>

      {adjust ? (
        <AdjustModal
          row={adjust.row}
          initDelta={adjust.initDelta}
          onClose={() => setAdjust(null)}
          onDone={() => { setAdjust(null); void load(); void loadWarnings(); }}
        />
      ) : null}
      </>
      )}
    </>
  );
}
